import { addDays, addHours, format, isToday, startOfWeek } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { WeekDay } from './types';
import { AFTERNOON_START_HOUR, isSplitDay } from './weekColumns';
import moment from 'moment';

const WEEK_LENGTH = 7;

export default function useWeekDays(reference?: Date) {
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'common' });
  const { language } = i18n;

  const weekDays: WeekDay[] = useMemo(() => {
    const locale = language === 'pt_BR' ? ptBR : enUS;
    const monday = startOfWeek(reference ?? moment.now(), { weekStartsOn: 1 });

    const toWeekDay = (date: Date, suffix?: string): WeekDay => {
      const abbreviation = format(date, 'EEE', { locale }).slice(0, 3);

      return {
        date,
        abbreviation: suffix ? `${abbreviation} ${suffix}` : abbreviation,
        dayNumber: format(date, 'd'),
        today: isToday(date),
      };
    };

    const dateToDomainWeekDay = (date: Date): WeekDay[] =>
      isSplitDay(date)
        ? [
            toWeekDay(date, t('dayPeriod.am')),
            toWeekDay(addHours(date, AFTERNOON_START_HOUR), t('dayPeriod.pm')),
          ]
        : [toWeekDay(date)];

    return Array.from({ length: WEEK_LENGTH }, (_, index) =>
      addDays(monday, index),
    ).flatMap(dateToDomainWeekDay);
  }, [reference, language, t]);

  return { weekDays };
}
